import { ethers } from 'ethers';
import { FANTOM_NETWORK } from '../config/constants';

class Web3Provider {
  constructor() {
    this.provider = null;
    this.signer = null;
    this.account = null;
    this.chainId = null;
  }

  async connect() {
    if (!window.ethereum) {
      throw new Error('No Ethereum wallet detected! Please install MetaMask.');
    }

    // Handle multiple wallet extensions
    let provider = window.ethereum;
    if (window.ethereum.providers) {
      // Multiple wallets detected, prefer MetaMask
      provider = window.ethereum.providers.find(p => p.isMetaMask) || window.ethereum.providers[0];
    }

    try {
      // Request account access with timeout
      const accounts = await Promise.race([
        provider.request({ method: 'eth_requestAccounts' }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Wallet connection timeout')), 10000)
        )
      ]);

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. Please unlock your wallet.');
      }
      
      // Create provider and signer
      this.provider = new ethers.BrowserProvider(provider);
      this.signer = await this.provider.getSigner();
      
      // Get account and network info
      this.account = await this.signer.getAddress();
      const network = await this.provider.getNetwork();
      this.chainId = Number(network.chainId);
      
      // Check if we're on Fantom network
      if (this.chainId !== 250) {
        await this.switchToFantom();
      }
      
      // Listen for account changes
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          this.disconnect();
        } else {
          this.account = accounts[0];
          window.location.reload();
        }
      });
      
      // Listen for chain changes
      window.ethereum.on('chainChanged', (chainId) => {
        window.location.reload();
      });
      
      return {
        account: this.account,
        chainId: this.chainId,
        provider: this.provider,
        signer: this.signer,
      };
    } catch (error) {
      console.error('Error connecting to wallet:', error);
      throw error;
    }
  }

  async switchToFantom() {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: FANTOM_NETWORK.chainId }],
      });
    } catch (switchError) {
      // This error code indicates that the chain has not been added to MetaMask
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [FANTOM_NETWORK],
          });
        } catch (addError) {
          throw new Error('Failed to add Fantom network to MetaMask');
        }
      } else {
        throw new Error('Failed to switch to Fantom network');
      }
    }
  }

  disconnect() {
    this.provider = null;
    this.signer = null;
    this.account = null;
    this.chainId = null;
  }

  isConnected() {
    return this.provider && this.signer && this.account;
  }

  getProvider() {
    // Return connected provider if available, otherwise return read-only provider
    if (this.provider) {
      return this.provider;
    }
    // Return a read-only provider for quotes and balance checks before wallet connection
    return new ethers.JsonRpcProvider('https://rpc.fantom.network');
  }

  getSigner() {
    return this.signer;
  }

  getAccount() {
    return this.account;
  }

  getChainId() {
    return this.chainId;
  }

  async getBalance(address = null) {
    if (!this.provider) throw new Error('Provider not connected');
    
    const targetAddress = address || this.account;
    const balance = await this.provider.getBalance(targetAddress);
    return ethers.formatEther(balance);
  }

  async getTokenBalance(tokenAddress, address = null) {
    if (!this.provider) throw new Error('Provider not connected');
    
    const targetAddress = address || this.account;
    const contract = new ethers.Contract(
      tokenAddress,
      ['function balanceOf(address) view returns (uint256)'],
      this.provider
    );
    
    const balance = await contract.balanceOf(targetAddress);
    return ethers.formatEther(balance);
  }
}

// Create singleton instance
const web3Provider = new Web3Provider();
export default web3Provider;